import { Alert, Button, Card, Form, Input, Space, Typography } from 'antd';
import { useState } from 'react';
import { functions } from '../../firebase';
import Message, { Type } from 'components/Notification/Message';

type FormValues = {
  recipients: string;
};

type DustBinderUsageReportResponse = {
  success: true;
  sentTo: string[];
  recipientCount: number;
  deviceCount: number;
  skippedCount: number;
  recipientSummaries: {
    email: string;
    orgCount: number;
    deviceCount: number;
  }[];
  range: {
    startDate: string;
    endDate: string;
  };
};

function parseRecipientInput(value: string): string[] {
  const recipients = value
    .split(/[,\n;]/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
  const invalid = recipients.filter((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

  if (invalid.length > 0) {
    throw new Error(`Ugyldig email: ${invalid[0]}`);
  }

  const external = recipients.filter(
    (email) => !email.endsWith('@akstotal.dk') && !email.endsWith('@aks2tal.com')
  );
  if (external.length > 0) {
    throw new Error(`Kun interne modtagere er tilladt: ${external[0]}`);
  }

  return Array.from(new Set(recipients));
}

export default function DustBinderUsageReportTestForm() {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DustBinderUsageReportResponse | null>(null);

  const onFinish = async (values: FormValues) => {
    try {
      const recipients = parseRecipientInput(values.recipients || '');
      if (recipients.length === 0) {
        throw new Error('Indtast mindst én modtager');
      }

      setLoading(true);
      setResult(null);
      Message({
        key: 'dustBinderUsageReport',
        message: 'Sender støvbinderforbrug',
        type: Type.LOADING,
      });

      const functionRef = functions.httpsCallable('sendDustBinderUsageReportEmail');
      const response = await functionRef({ recipients });
      const data = response.data as DustBinderUsageReportResponse;

      setResult(data);
      Message({
        key: 'dustBinderUsageReport',
        message: 'Email sendt',
        description: `Sendt til ${data.recipientCount} modtager(e)`,
        type: Type.SUCCESS,
      });
    } catch (error: any) {
      Message({
        key: 'dustBinderUsageReport',
        message: 'Kunne ikke sende email',
        description: error?.message || 'Der skete en fejl',
        type: Type.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Støvbinderforbrug email" size="small">
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="Testmodtagere"
          name="recipients"
          rules={[{ required: true, message: 'Indtast mindst én email' }]}
          extra="Kun @akstotal.dk og @aks2tal.com. Hver modtager får kun de organisationer eller sensorer, de har adgang til."
        >
          <Input.TextArea
            rows={3}
            placeholder="navn@akstotal.dk, navn@aks2tal.com"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item>
          <Space wrap>
            <Button type="primary" htmlType="submit" loading={loading}>
              Send scoped test email
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Alert
        type="info"
        showIcon
        message="Fredagskørsel"
        description={
          <div>
            <Typography.Text>Fredag 07:00 Europe/Copenhagen</Typography.Text>
            <br />
            <Typography.Text>
              Modtagere udledes automatisk fra direkte org-adgang og sensoransvarlige. Listen her
              gemmes ikke til fredag.
            </Typography.Text>
          </div>
        }
        style={{ marginBottom: 12 }}
      />

      {result && (
        <Alert
          type="success"
          showIcon
          message="Rapporten blev sendt"
          description={
            <div>
              <Typography.Text>
                Periode: {result.range.startDate} - {result.range.endDate}
              </Typography.Text>
              <br />
              <Typography.Text>Modtagere: {result.sentTo.join(', ')}</Typography.Text>
              <br />
              <Typography.Text>
                Sensorer: {result.deviceCount}. Sprunget over: {result.skippedCount}.
              </Typography.Text>
              {result.recipientSummaries.length > 0 && (
                <>
                  <br />
                  <Typography.Text>
                    Scope:{' '}
                    {result.recipientSummaries
                      .map(
                        (summary) =>
                          `${summary.email}: ${summary.deviceCount} sensorer/${summary.orgCount} orgs`
                      )
                      .join(', ')}
                  </Typography.Text>
                </>
              )}
            </div>
          }
        />
      )}
    </Card>
  );
}
